import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Infography from "@common/components/Infography";
import { TextField } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import NaturePeopleOutlinedIcon from "@mui/icons-material/NaturePeopleOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axiosInstance from "@common/utils/axiosInstance";
import apiUrl from "@common/utils/apiURL";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

// --- Count-up hook ---
function useCountUp(target, duration = 1800, inView = false) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);
  useEffect(() => {
    if (!inView || hasRun.current) return;
    hasRun.current = true;
    const numeric = parseInt(target, 10);
    if (isNaN(numeric)) { setCount(target); return; }
    const suffix = target.replace(String(numeric), "");
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(ease * numeric) + suffix);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);
  return count === 0 ? "0" : count;
}

function StatItem({ number, label }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const animated = useCountUp(number, 1600, inView);
  return (
    <div className="cr-stat" ref={ref}>
      <span className="cr-stat__number">{animated}</span>
      <span className="cr-stat__label">{label}</span>
    </div>
  );
}

const valuesData = [
  {
    Icon: NaturePeopleOutlinedIcon,
    title: "Purposeful Work",
    desc: "Craft sustainable luxury textiles that blend elegance with environmental responsibility. Every project contributes to a greener future.",
  },
  {
    Icon: TrendingUpOutlinedIcon,
    title: "Growth & Learning",
    desc: "Access mentorship, skill-building workshops, and clear career pathways. We invest in your professional development from day one.",
  },
  {
    Icon: LightbulbOutlinedIcon,
    title: "Innovation",
    desc: "Work in a design-led environment with cutting-edge R&D facilities. Push boundaries in textile technology and sustainable manufacturing.",
  },
  {
    Icon: GroupsOutlinedIcon,
    title: "Community",
    desc: "Be part of a team that gives back through initiatives like Project Shiksha, empowering education and creating lasting social impact.",
  },
];



function useRipple() {
  const [ripples, setRipples] = useState([]);
  const addRipple = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples((r) => [...r, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 600);
  }, []);
  return [ripples, addRipple];
}

export default function Careers() {
  const navigate = useNavigate();
  const [activeDepartment, setActiveDepartment] = useState("All");
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const positionsRef = useRef(null);
  const recaptchaRef = useRef(null);
  const resumeInputRef = useRef(null);
  const [heroRipples, addHeroRipple] = useRipple();

  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 600], [0, 90]);
  const heroOpacity = useTransform(scrollY, [0, 320], [1, 0]);

  useEffect(() => {
    axiosInstance.get(apiUrl.job.getAll)
      .then((res) => setJobs(res.data))
      .catch((err) => console.error("Failed to fetch jobs:", err))
      .finally(() => setJobsLoading(false));
  }, []);

  const departments = ["All", ...Array.from(new Set(jobs.map((j) => j.category)))];

  const filteredPositions =
    activeDepartment === "All"
      ? jobs
      : jobs.filter((p) => p.category === activeDepartment);

  const scrollToPositions = () =>
    positionsRef.current?.scrollIntoView({ behavior: "smooth" });

  const formik = useFormik({
    initialValues: { fullName: "", email: "", phone: "", position: "", message: "", resume: null, terms: false, recaptcha: "" },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email address").required("Required"),
      phone: Yup.string().matches(/^[0-9]{10}$/, "Must be a valid phone number").required("Required"),
      position: Yup.string().required("Required"),
      message: Yup.string().max(500, "Must be 500 characters or less"),
      resume: Yup.mixed()
        .required("Resume is required")
        .test("fileSize", "File must be less than 5 MB", (v) => !v || v.size <= 5 * 1024 * 1024)
        .test("fileType", "Only PDF, DOC, DOCX allowed", (v) => !v || ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(v.type)),
      terms: Yup.boolean().oneOf([true], "You must accept the terms and conditions").required("Required"),
      recaptcha: Yup.string().required("reCAPTCHA is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setTimeout(() => {
        formik.resetForm();
        if (resumeInputRef.current) resumeInputRef.current.value = "";
        setOpenConfirmationModal(true);
        recaptchaRef.current?.reset();
        setSubmitting(false);
        setTimeout(() => setOpenConfirmationModal(false), 2000);
      }, 500);
    },
  });

  const listVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, y: -12, transition: { duration: 0.2 } },
  };

  return (
    <div className="careers-page">

      {/* ========== HERO — variant-typ-3 pattern ========== */}
      <div className="bs-section-banner bs-section-banner--variant-typ-3 cr-hero">
        <div className="bs-section-banner__media-wrap">
          <motion.img
            src="https://res.cloudinary.com/dci1aiukm/image/upload/q_auto/f_auto/quality-standards.jpg"
            alt="Sansaar workplace"
            className="bs-section-banner__image cr-hero__image"
            style={{ y: heroImgY }}
          />
        </div>

        <div className="bs-section-banner__info-wrap">
          <div className="bs-section-banner__desc-wrap">
            <motion.span
              className="cr-hero__eyebrow"
              initial={{ opacity: 0, letterSpacing: "10px" }}
              animate={{ opacity: 1, letterSpacing: "5px" }}
              transition={{ duration: 1.2, delay: 0.2 }}
            >
              Careers at Sansaar
            </motion.span>
            <motion.h1
              className="bs-section__title"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              Weave your future
              <br />
              with Sansaar
            </motion.h1>
            <motion.p
              className="bs-section__desc"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              Join the house of D'Decor and help craft luxury textiles that
              inspire conscious living across the world.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.95 }}
          >
            <button
              className="bs-btn bs-btn__btn-solid bs-btn__btn-solid--inverse bs-section-banner__action cr-hero__cta"
              onClick={(e) => { addHeroRipple(e); scrollToPositions(); }}
            >
              Explore Open Positions
              {heroRipples.map((r) => (
                <span key={r.id} className="cr-hero__ripple" style={{ left: r.x, top: r.y }} />
              ))}
            </button>
          </motion.div>
        </div>

        {/* scroll indicator */}
        <motion.div className="cr-hero__scroll" style={{ opacity: heroOpacity }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
          <span className="cr-hero__scroll-label">Scroll</span>
          <span className="cr-hero__scroll-track"><span className="cr-hero__scroll-dot" /></span>
        </motion.div>
      </div>

      {/* ========== WHY WORK WITH US — bs-section--typ-grey ========== */}
      <div className="bs-section bs-section--typ-grey">
        <motion.div
          className="bs-section__section-head"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="bs-section__title" style={{ textTransform: "none", paddingBottom: "16px" }}>Our Promise to You</h2>
          <p className="bs-section__desc">
            At Sansaar, you're not just building a career — you're shaping the
            future of sustainable luxury. Here's what makes us different.
          </p>
        </motion.div>

        <div className="bs-section__section-cont">
          <ul className="cr-values__grid">
            {valuesData.map(({ Icon, title, desc }, i) => (
              <motion.li
                key={i}
                className="cr-values__card"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.12 }}
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
              >
                <motion.div
                  className="cr-values__icon"
                  whileHover={{ scale: 1.15, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 300, damping: 14 }}
                >
                  <Icon fontSize="inherit" />
                </motion.div>
                <h3 className="cr-values__card-title">{title}</h3>
                <p className="cr-values__card-desc">{desc}</p>
                <span className="cr-values__shine" aria-hidden="true" />
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* ========== OUR STORY — full cream bg, no padding, mirrors OurJourney ========== */}
      <div className="bs-section bs-section--typ-grey" style={{ "paddingTop": "24px!important" }}>
        <div className="bs-section__section-cont">
          <Infography
            imageUrl="sansaar-desk"
            title="Our Story"
            desc="Founded by the house of D'Decor, Sansaar is a tribute to conscious living. With state-of-the-art manufacturing, global reach, and an unwavering commitment to sustainability, we've built a brand that stands for both luxury and responsibility."
            desc2="From our design studios in Mumbai to our production facilities at Tarapur, every team member plays a vital role in crafting textiles that transform spaces and inspire mindful living worldwide."
            reverse={true}
            variant="no-space"
          />
        </div>
      </div>

      {/* ========== LIFE AT SANSAAR — bs-section--typ-grey ========== */}
      <div className="bs-section bs-section--typ-grey">
        {/* <motion.div
          className="bs-section__section-head"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="bs-section__title">Life at Sansaar</h2>
          <label className="mod-label">Culture & Community</label>
          <p className="bs-section__desc">
            A workplace where creativity thrives, collaboration is celebrated,
            and every voice matters.
          </p>
        </motion.div> */}

        <div className="bs-section__section-cont">
          {/* <motion.div
            className="cr-gallery"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            viewport={{ once: true }}
          >
            <div className="cr-gallery__item cr-gallery__item--large">
              <img src="https://res.cloudinary.com/dci1aiukm/image/upload/v1780984343/team.png" alt="Team collaboration" loading="lazy" />
              <span className="cr-gallery__label">Collaboration</span>
            </div>
            <div className="cr-gallery__item cr-gallery__item--creativity">
              <img src="https://res.cloudinary.com/dci1aiukm/image/upload/v1780984343/sansaar-billboard.png" alt="Creative workspace" loading="lazy" />
              <span className="cr-gallery__label">Creativity</span>
            </div>
            <div className="cr-gallery__item">
              <img src="https://res.cloudinary.com/dci1aiukm/image/upload/v1780984349/laptop1.png" alt="Team celebrations" loading="lazy" />
              <span className="cr-gallery__label">Celebrations</span>
            </div>
          </motion.div> */}
          {/* 
          <motion.div
            className="cr-stats"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            viewport={{ once: true }}
          >
            <StatItem number="400+" label="Stores Nationwide" />
            <StatItem number="25+" label="Years of Legacy" />
            <StatItem number="11" label="Certifications" />
            <StatItem number="50+" label="Countries Served" />
          </motion.div> */}
        </div>
      </div>

      {/* ========== OPEN POSITIONS — bs-section--typ-grey ========== */}
      <div className="bs-section bs-section--typ-grey" ref={positionsRef}>
        <motion.div
          className="bs-section__section-head"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="bs-section__title">Open Positions</h2>
          <label className="mod-label">Join the Team</label>
          <p className="bs-section__desc">
            Explore our current openings and find where your skills and passion
            align with our mission.
          </p>
        </motion.div>

        <div className="bs-section__section-cont">
          {/* Department filters */}
          <div className="cr-filters">
            {departments.map((dept) => (
              <button
                key={dept}
                className={`cr-filters__btn ${activeDepartment === dept ? "cr-filters__btn--active" : ""}`}
                onClick={() => setActiveDepartment(dept)}
              >
                {dept}
                {activeDepartment === dept && (
                  <motion.span
                    className="cr-filters__pill"
                    layoutId="cr-filter-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Job listings */}
          {jobsLoading ? (
            <div className="cr-positions__loading">Loading positions…</div>
          ) : jobs.length === 0 ? (
            <div className="cr-positions__empty">NO JOB POST available</div>
          ) : filteredPositions.length === 0 ? (
            <div className="cr-positions__empty">No open positions in this category right now.</div>
          ) : null}
          <AnimatePresence mode="wait">
            <motion.ul
              key={activeDepartment}
              className="cr-positions"
              variants={listVariants}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, transition: { duration: 0.12 } }}
            >
              {filteredPositions.map((pos) => (
                <motion.li
                  key={pos.id}
                  className="cr-positions__card"

                  variants={cardVariants}
                  layout
                  whileHover={{ x: 3, transition: { duration: 0.18 } }}
                  onClick={() => navigate(`/careers/${pos.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/careers/${pos.id}`); }}
                >
                  <span className="cr-positions__accent" />
                  <div className="cr-positions__info">
                    <h3 className="cr-positions__title">{pos.title}</h3>
                    <div className="cr-positions__meta">
                      <span className="cr-positions__dept">{pos.category}</span>
                      <span className="cr-positions__dot" aria-hidden="true" />
                      <span className="cr-positions__meta-item">
                        <PlaceOutlinedIcon className="cr-positions__meta-icon" />
                        {pos.location}
                      </span>
                      <span className="cr-positions__dot" aria-hidden="true" />
                      <span className="cr-positions__meta-item">
                        <WorkOutlineOutlinedIcon className="cr-positions__meta-icon" />
                        {pos.type}
                      </span>
                    </div>
                  </div>
                  <span className="cr-positions__view-hint">
                    View Details
                    <svg className="cr-positions__arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>
      </div>

      {/* ========== GENERAL APPLICATION — bs-section white ========== */}
      <div className="bs-section">
        <motion.div
          className="bs-section__section-head"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="bs-section__title">Don't See Your Role?</h2>
          <label className="mod-label">General Application</label>
          <p className="bs-section__desc">
            We're always looking for exceptional talent. Send us your details
            and we'll reach out when the right opportunity arises.
          </p>
        </motion.div>

        <motion.div
          className="bs-section__section-cont cr-form-wrap"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          viewport={{ once: true }}
        >
          <form onSubmit={formik.handleSubmit}>
            <div className="bs-form">
              <div className={`bs-form__form-group ${formik.touched.fullName && formik.errors.fullName ? "bs-form__error" : ""}`}>
                <TextField id="fullName" name="fullName" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.fullName} label="Full Name" className="bs-form__form-control" />
                {formik.touched.fullName && formik.errors.fullName && <div className="bs-form__error-text">{formik.errors.fullName}</div>}
              </div>
              <div className={`bs-form__form-group ${formik.touched.email && formik.errors.email ? "bs-form__error" : ""}`}>
                <TextField id="careerEmail" name="email" type="email" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.email} label="E-mail" className="bs-form__form-control" />
                {formik.touched.email && formik.errors.email && <div className="bs-form__error-text">{formik.errors.email}</div>}
              </div>
              <div className={`bs-form__form-group ${formik.touched.phone && formik.errors.phone ? "bs-form__error" : ""}`}>
                <TextField id="careerPhone" name="phone" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.phone} label="Phone" className="bs-form__form-control" />
                {formik.touched.phone && formik.errors.phone && <div className="bs-form__error-text">{formik.errors.phone}</div>}
              </div>
              <div className={`bs-form__form-group ${formik.touched.position && formik.errors.position ? "bs-form__error" : ""}`}>
                <TextField id="careerPosition" name="position" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.position} label="Position of Interest" className="bs-form__form-control" />
                {formik.touched.position && formik.errors.position && <div className="bs-form__error-text">{formik.errors.position}</div>}
              </div>
              <div className={`bs-form__form-group bs-form__form-group--full-width textarea ${formik.touched.message && formik.errors.message ? "bs-form__error" : ""}`}>
                <TextField id="careerMessage" name="message" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.message} label="Tell us about yourself" className="bs-form__form-control bs-form__form-control--text-area" multiline />
                {formik.touched.message && formik.errors.message && <div className="bs-form__error-text">{formik.errors.message}</div>}
              </div>
              {/* Resume Upload */}
              <div className={`bs-form__form-group bs-form__form-group--full-width ${formik.touched.resume && formik.errors.resume ? "bs-form__error" : ""}`}>
                <div
                  className={`cr-upload${formik.values.resume ? " cr-upload--has-file" : ""}`}
                  onClick={() => resumeInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("cr-upload--drag-over"); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("cr-upload--drag-over"); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("cr-upload--drag-over");
                    const file = e.dataTransfer.files?.[0];
                    if (file) formik.setFieldValue("resume", file);
                  }}
                >
                  <input
                    ref={resumeInputRef}
                    id="careerResume"
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="cr-upload__input"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0];
                      formik.setFieldValue("resume", file || null);
                    }}
                  />
                  <AttachFileIcon className="cr-upload__icon" />
                  {formik.values.resume ? (
                    <div className="cr-upload__file-info">
                      <span className="cr-upload__file-name">{formik.values.resume.name}</span>
                      <span className="cr-upload__file-size">{(formik.values.resume.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ) : (
                    <div className="cr-upload__placeholder">
                      <span className="cr-upload__title">Upload Resume</span>
                      <span className="cr-upload__hint">Drag & drop or click to browse — PDF, DOC, DOCX (max 5 MB)</span>
                    </div>
                  )}
                </div>
                {formik.touched.resume && formik.errors.resume && <div className="bs-form__error-text">{formik.errors.resume}</div>}
              </div>

              <div className={`bs-form__form-bottom-wrap ${formik.touched.terms && formik.errors.terms ? "bs-form__error" : ""}`}>
                <div className="bs-checkbox">
                  <input type="checkbox" id="careerTerms" name="terms" onChange={formik.handleChange} onBlur={formik.handleBlur} checked={formik.values.terms} />
                  <label className="bs-checkbox__label" htmlFor="careerTerms">
                    I agree to <Link to="/tnc" className="bs-checkbox__link">terms & Conditions</Link>
                  </label>
                  {formik.touched.terms && formik.errors.terms && <div className="bs-form__error-text">{formik.errors.terms}</div>}
                </div>
                <div className={formik.touched.recaptcha && formik.errors.recaptcha ? "bs-form__error" : ""}>
                  <ReCAPTCHA ref={recaptchaRef} sitekey="6LcSFQsqAAAAAJPkl0Nyx8wH7UMvWVxMTK9O50yd" onChange={(v) => formik.setFieldValue("recaptcha", v)} />
                  {formik.touched.recaptcha && formik.errors.recaptcha && <div className="bs-form__error-text">{formik.errors.recaptcha}</div>}
                </div>
                <button className="bs-btn bs-btn__btn-solid" type="submit" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? "Submitting…" : "Submit Application"}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Confirmation Modal */}
      <Modal open={openConfirmationModal} onClose={() => setOpenConfirmationModal(false)} className="bs-modal bs-modal--typ-sm">
        <Box className="bs-modal__wrapper" sx={modalStyle}>
          <div className="bs-modal__content">
            <div className="mod-message">
              <span className="icon icon-check-circle mod-message__icon" />
              <p className="mod-message__title">Application Received!</p>
              <p className="mod-message__desc">Thank you for your interest. We'll review your application and get back to you soon.</p>
            </div>
          </div>
        </Box>
      </Modal>

    </div>
  );
}
